import { Button, Col, Popover, Row, Table, Tooltip } from 'antd';
import { useNavigate } from 'react-router';
import { incidencePaths } from 'constants/routePaths';
import { ColumnsType, TableProps } from 'antd/es/table';
import {
  CheckCircleFilled,
  CloseCircleFilled,
  DeleteFilled,
} from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { IncidenceCategory } from 'types/incidences';
import { getCategories, removeCategory } from 'api/incidence.api';
import * as Sentry from '@sentry/react';
import { IncidenceCategoryPreview } from './IncidenceCategoryPreview';
import notificationHook from 'hooks/notificationHook';
import statusMessages from 'statusMessages';
import { ApiError } from 'types';
import { TableEditButton } from 'components/TableEditButton/TableEditButton';

type IncidencesCatalogueProps = {
  categories: IncidenceCategory[];
};

const IncidenceCategories = ({ categories }: IncidencesCatalogueProps) => {
  const navigate = useNavigate();
  const [incidenceReasonsData, setIncidenceReasonsData] = useState<
    IncidenceCategory[]
  >([]);
  const tableProps: TableProps<IncidenceCategory> = {
    bordered: true,
    pagination: { position: ['bottomCenter'] },
    tableLayout: 'fixed',
    size: 'small',
    className: 'table-ps',
  };

  useEffect(() => {
    setIncidenceReasonsData(categories);
  }, [categories]);

  const updateIncidentReasonsList = () => {
    getCategories().then((data) => setIncidenceReasonsData(data.data));
  };

  const deleteIncidentReason = async (id: string) => {
    try {
      await removeCategory(id);
      updateIncidentReasonsList();
      notificationHook({
        type: 'success',
        message: 'Se ha eliminado la categoria de incidencia con éxito',
      });
    } catch (error: unknown) {
      Sentry.captureException(error);
      const currentStatus = (error as ApiError).statusCode;
      notificationHook({
        message: statusMessages.incidence[
          currentStatus ? currentStatus : 500
        ] as string,
        type: 'error',
      });
    }
  };

  const columns: ColumnsType<IncidenceCategory> = [
    {
      title: 'Incidencia',
      key: 'abbr',
      dataIndex: 'abbr',
      width: 120,
    },
    {
      title: 'Color',
      key: 'color',
      dataIndex: 'color',
      width: 120,
      render: (_, record) => (
        <Row justify="center" className="incidences-catalogue-color">
          <Col>
            <IncidenceCategoryPreview
              text=""
              bgColor={record.color}
              txtColor={record.txtColor}
              showText={false}
            />
          </Col>
        </Row>
      ),
    },
    {
      title: 'Descripción',
      key: 'desc',
      dataIndex: 'desc',
    },
    {
      title: 'Solicitable',
      key: 'solicitable',
      dataIndex: 'solicitable',
      width: 140,
      render: (solicitable: boolean | undefined, record) => {
        const isSolicitable = Boolean(solicitable);

        return (
          <Tooltip
            title={
              isSolicitable
                ? 'Disponible para solicitud docente'
                : 'No disponible para solicitud docente'
            }
          >
            <span
              className={`incidences-catalogue-solicitable incidences-catalogue-solicitable--${isSolicitable ? 'active' : 'inactive'}`}
              data-cy={`incidence-category-solicitable-${record._id}`}
            >
              {isSolicitable ? <CheckCircleFilled /> : <CloseCircleFilled />}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Opciones',
      key: 'options',
      width: 140,
      render: (_, record) => (
        <div className="option-buttons incidences-catalogue-actions">
          <TableEditButton
            id={record._id}
            dataCy={`incidence-category-edit-btn-${record._id}`}
            onClick={() => {
              navigate(
                `${incidencePaths.basePath}/${incidencePaths.catalogue}/${incidencePaths.catalogueEdit}/${record._id}`
              );
            }}
          />
          <div title="Eliminar">
            <Popover
              content={
                record.protected ? (
                  <p>No es posible eliminar este tipo de incidencia</p>
                ) : (
                  <Button
                    disabled={record.protected}
                    danger
                    type="primary"
                    onClick={() => {
                      deleteIncidentReason(record._id);
                    }}
                  >
                    Confirmar
                  </Button>
                )
              }
              trigger="click"
              placement="left"
            >
              <Button
                id={record._id}
                className="error-txt"
                data-cy={`incidence-category-delete-btn-${record._id}`}
              >
                <DeleteFilled />
              </Button>
            </Popover>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="incidences-catalogue-page">
      <Row
        justify="space-between"
        align="middle"
        className="incidences-catalogue-header"
        gutter={[16, 16]}
      >
        <Col>
          <h2 className="title">Catálogo de tipos de incidencias</h2>
        </Col>
        <Col>
          <Button
            id="btnNewIncident"
            type="primary"
            data-cy="incidence-category-create-btn"
            onClick={() => {
              navigate(
                `${incidencePaths.basePath}/${incidencePaths.catalogue}/${incidencePaths.catalogueNew}`
              );
            }}
          >
            Crear tipo
          </Button>
        </Col>
      </Row>

      <Table
        {...tableProps}
        columns={columns}
        dataSource={incidenceReasonsData}
        rowKey={'_id'}
        className="table-ps incidences-catalogue-table"
        data-cy="incidence-category-table"
        locale={{
          emptyText: 'No hay tipos de incidencia registrados.',
        }}
      />
    </div>
  );
};

export default IncidenceCategories;
